import { InfoCircleOutlined, LockOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Avatar, Button, Drawer, Input, Popconfirm, Popover, Table, Tooltip, Typography } from 'antd'
import { ColumnType } from 'antd/es/table'
import { produce } from 'immer'
import { useAtom } from 'jotai'
import { isEqual, isNil, isNull, omit, partition, pick, uniq } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'

import { getPointPermission, MosaicComponent, UsersSearch } from '@/components'
import { userInfoAtom, usersAtom } from '@/modules/user'
import {
  DepartmentProjectWorkResource,
  PAGE_RESOURCE_POINTS_ENUM,
  ResourceType,
  Team,
  WorkResourceRowType,
} from '@/schemas'

import ss from '../click-away/click-away.module.less'
// import { ClickAwayInputNumber } from '../click-away/click-away-input-number.component'
import { ClickAwayUserSearch } from '../click-away/click-away-user-search.component'
import { ClickAwayWeekInputNumber } from '../click-away/click-away-week-input-number.component'
import s from '../special-project/special-project-work-resource.module.less'
import {
  getCreatingRecordFromTree,
  getIsValidWeekToUpsert,
  getParentByKey,
  getRecordByKey,
  setCreatingRecordToSubTree,
} from '../special-project/special-project-work-resource.util'

export interface DepartmentProjectWorkResourceInput extends DepartmentProjectWorkResource {
  isCreating?: boolean
  parentKey?: string | null
}

const TdCell = (props: any) => {
  // onMouseEnter, onMouseLeave在数据量多的时候，会严重阻塞表格单元格渲染，严重影响性能
  return <td {...omit(props, ['onMouseEnter', 'onMouseLeave'])} />
}

const getSpecificProjectUrl = (selfId?: string, resourceType?: ResourceType) => {
  switch (resourceType) {
    case ResourceType.CUSTOMER: {
      return `${window.location.origin}/customer/${selfId}`
    }
    case ResourceType.DEVLOP: {
      return `${window.location.origin}/devlop/${selfId}`
    }
    case ResourceType.PRODUCTION: {
      return `${window.location.origin}/production/${selfId}`
    }
    default: {
      return ''
    }
  }
}

export const DepartmentProjectWorkResourceContainer: React.FC<{
  team: Team
  weekDate: { year: number; week: number; startDate: string; endDate: string }
  startDate: string
  endDate: string
}> = React.forwardRef(({ team, startDate, endDate, weekDate }, ref) => {
  const [users] = useAtom(usersAtom)
  const [viewer] = useAtom(userInfoAtom)
  const { t } = useTranslation()
  const { canCreate, canUpdate, canDelete } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, team.permissions)

  const [openDrawer, setOpenDrawer] = React.useState<{
    record: DepartmentProjectWorkResourceInput
    readonly: boolean
  }>()
  const [drawerContent, setDrawerContent] = React.useState('')
  const [expandKeys, setExpandKeys] = React.useState<string[]>([])

  const [records, setRecords] = React.useState<DepartmentProjectWorkResourceInput[]>([])

  const isSuperAdmin = viewer.isSuperAdmin
  const {
    data,
    refresh,
    loading: getLoading,
  } = useRequest<DepartmentProjectWorkResource[]>(
    () => ({
      url: `/api/work-resource/getDepartmentWorkResource?${new URLSearchParams({
        departmentId: team.departmentId,
        type: 'project',
        startDate,
        endDate,
      } as any)}`,
      method: 'get',
    }),
    {
      refreshDeps: [startDate, endDate, team],
    }
  )

  const { run, loading: creatingLoading } = useRequest(
    (data) => ({
      url: `/api/work-resource/createSpecificProjectUserWorkResource`,
      data: {
        ...data,
      },
      method: 'post',
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: deleteRun, loading: deleteLoading } = useRequest(
    (data) => ({
      url: `/api/work-resource/deleteWorkResource?${new URLSearchParams(data)}`,
      method: 'delete',
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: updateRun, loading: updateLoading } = useRequest(
    (data) => ({
      url: '/api/work-resource/updateWorkResource',
      method: 'patch',
      data,
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: commentRun, loading: commentLoading } = useRequest(
    (data) => ({
      url: '/api/work-resource/upsertWeekWorkResourceComment',
      method: 'post',
      data,
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  React.useEffect(() => {
    if (data) {
      setRecords((prev) => {
        const creatingData: DepartmentProjectWorkResourceInput[] = []
        getCreatingRecordFromTree(prev, creatingData)
        const [rootCreatingData, subTreeCreatingData] = partition(creatingData, (d) => isNull(d.parentKey))

        const newState = produce(data, (draft) => {
          setCreatingRecordToSubTree(draft, subTreeCreatingData)
        })
        return [...(newState as DepartmentProjectWorkResourceInput[]), ...rootCreatingData]
      })
    }
  }, [data])

  React.useEffect(() => {
    const week = openDrawer?.record?.weeks?.[0]
    setDrawerContent(week?.comment?.comment || '')
  }, [openDrawer])

  React.useImperativeHandle(ref, () => {
    return {
      refresh,
    }
  })

  const columns: ColumnType<DepartmentProjectWorkResourceInput>[] = [
    {
      title: t('work.resource.table.title.project'),
      dataIndex: 'project',
      key: 'project',
      width: 200,
      render: (_, record) => {
        const url = getSpecificProjectUrl(record.project?.selfId, record.project?.resourceType)
        return (
          <div className='flex gap-2 items-baseline'>
            <div className='max-w-40 whitespace-nowrap overflow-hidden overflow-ellipsis'>
              {record.project ? (
                <Tooltip title={record.project.name}>
                  <Typography.Link href={url} ellipsis rel='noopener noreferrer' target='_blank'>
                    {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, record.project.permissions).canRead ? (
                      record.project.name
                    ) : (
                      <MosaicComponent content={record.project.name} />
                    )}
                  </Typography.Link>
                </Tooltip>
              ) : null}
            </div>
          </div>
        )
      },
      shouldCellUpdate: (curr, prev) => !isEqual(curr.project, prev.project),
    },
    {
      title: t('work.resource.table.title.subproject'),
      dataIndex: 'subproject',
      key: 'subproject',
      width: 200,
      render: (_, record) => {
        if (![WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type) && !record.isCreating) {
          return null
        }
        const includeEmails = team.departmentId
          ? users.filter((u) => u.departmentIds.includes(team.departmentId))
          : undefined

        return (
          <div className='flex gap-2 items-center'>
            <div className='max-w-40 whitespace-nowrap overflow-hidden overflow-ellipsis'>
              {record.subproject ? (
                <Tooltip title={record.subproject?.name}>
                  <Typography.Link
                    href={`${window.location.origin}/project/${record.subproject?.selfId}`}
                    ellipsis
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, record.subproject.permissions).canRead ? (
                      record.subproject?.name
                    ) : (
                      <MosaicComponent content={record.subproject?.name} />
                    )}
                  </Typography.Link>
                </Tooltip>
              ) : (
                <Tooltip title={record.subprojectName}>
                  <span>{record.subprojectName}</span>
                </Tooltip>
              )}
            </div>
            {!record.isCreating && !record.isArchived && (
              <span>
                {canCreate ? (
                  <Popover
                    trigger='click'
                    title={
                      <Typography.Text className='font-medium text-[16px] text-[#191919] leading-[24px]'>
                        {t('add users')}
                      </Typography.Text>
                    }
                    content={
                      <div>
                        <UsersSearch
                          onChange={(v) => {
                            if (v) {
                              setRecords((prev) => {
                                const newState = produce(prev, (draft) => {
                                  // 获取当前节点
                                  const records: DepartmentProjectWorkResourceInput[] = []
                                  getRecordByKey(record.key, draft, records)
                                  const [currentRecord] = records

                                  const parents: DepartmentProjectWorkResourceInput[] = []
                                  getParentByKey(record.key, draft, parents)
                                  const [parent] = parents

                                  const newRecord = {
                                    projectWorkResourceSelfId: parent?.projectWorkResourceSelfId || null,
                                    key: `${currentRecord.key}-${v4()}`,
                                    type: WorkResourceRowType.User,
                                    subprojectWorkResourceSelfId: record.subprojectWorkResourceSelfId,
                                    userWorkResourceSelfId: '',
                                    subprojectName: record!.subprojectName,
                                    subprojectSelfId: record!.subprojectSelfId,
                                    departmentId: team.departmentId,
                                    departmentName: team.name,
                                    email: v,
                                    startDate: (parent?.project?.startDate
                                      ? moment(parent?.project?.startDate)
                                      : parent?.project?.endDate
                                        ? moment(parent?.project?.endDate).subtract(1, 'day')
                                        : moment()
                                    ).format('YYYY-MM-DD'),
                                    endDate: (parent?.project?.endDate
                                      ? moment(parent?.project?.endDate)
                                      : parent?.project?.startDate
                                        ? moment(parent?.project?.startDate).add(1, 'day')
                                        : moment()
                                    ).format('YYYY-MM-DD'),
                                    predictWorkload: null,
                                    actualWorkload: null,
                                    project: parent?.project,
                                    subproject: record.subproject,
                                    quarters: [],
                                    weeks: [],
                                    isCreating: true,
                                    parentKey: currentRecord.key,
                                  }

                                  currentRecord['children'] = currentRecord.children
                                    ? [...currentRecord.children, newRecord]
                                    : [newRecord]
                                })
                                return newState
                              })
                              setExpandKeys((prev) => uniq([...prev, record.key]))
                            }
                          }}
                          includeEmails={includeEmails}
                          placeholder={t('search users')}
                          style={{ width: 200 }}
                        />
                      </div>
                    }
                  >
                    <PlusCircleOutlined />
                  </Popover>
                ) : (
                  <PlusCircleOutlined className='cursor-not-allowed' />
                )}
              </span>
            )}
          </div>
        )
      },
      shouldCellUpdate: (curr, prev) =>
        !isEqual(curr.subproject, prev.subproject) ||
        curr.subprojectName !== prev.subprojectName ||
        curr.subprojectSelfId !== prev.subprojectSelfId,
    },
    {
      title: t('work.resource.table.title.people'),
      dataIndex: 'email',
      key: 'email',
      width: 160,
      render: (value, record) => {
        if (record.type === WorkResourceRowType.Resource) {
          const emails = (record.children || []).flatMap((child) => child.children || []).map((child) => child.email)
          const filteredUsers = users.filter((u) => emails.includes(u.email))
          return (
            <Avatar.Group
              size='small'
              maxCount={5}
              maxStyle={{
                color: '#f56a00',
                backgroundColor: '#fde3cf',
                cursor: 'pointer',
              }}
            >
              {filteredUsers.map((u) => {
                return (
                  <Tooltip key={u.email} title={u.name}>
                    <Avatar src={u.avatar} className='cursor-pointer' />
                  </Tooltip>
                )
              })}
            </Avatar.Group>
          )
        }
        if ([WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type)) {
          const emails = (record.children || []).map((r) => r.email)
          const filteredUsers = users.filter((u) => emails.includes(u.email))
          return (
            <Avatar.Group
              size='small'
              maxCount={5}
              maxStyle={{
                color: '#f56a00',
                backgroundColor: '#fde3cf',
                cursor: 'pointer',
              }}
            >
              {filteredUsers.map((u) => {
                return (
                  <Tooltip key={u.email} title={u.name}>
                    <Avatar src={u.avatar} className='cursor-pointer' />
                  </Tooltip>
                )
              })}
            </Avatar.Group>
          )
        }
        if (record.type !== WorkResourceRowType.User) {
          return null
        }
        const isUserNotEmployee =
          record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
        const readonly = record.isArchived || !canUpdate || isUserNotEmployee
        const includeEmails = team.departmentId
          ? users.filter((u) => u.departmentIds.includes(team.departmentId))
          : undefined
        return (
          <ClickAwayUserSearch
            readonly={readonly}
            required={true}
            isActive={record.isCreating}
            value={value}
            includeEmails={includeEmails}
            onChange={(v) => {
              setRecords((prev) =>
                produce(prev, (draft) => {
                  const currentRecords: DepartmentProjectWorkResourceInput[] = []
                  getRecordByKey(record.key, draft, currentRecords)
                  const [currentRecord] = currentRecords

                  if (currentRecord) {
                    currentRecord.email = v
                  }
                })
              )
              if (record.userWorkResourceSelfId) {
                const parents: DepartmentProjectWorkResourceInput[] = []
                getParentByKey(record.key, records, parents)
                const [parent] = parents
                const grandparents: DepartmentProjectWorkResourceInput[] = []
                getParentByKey(parent.key, records, grandparents)
                const [project] = grandparents

                updateRun({
                  userWorkResourceSelfId: record.userWorkResourceSelfId,
                  projectWorkResourceSelfId: project.projectWorkResourceSelfId,
                  email: v,
                })
              }
            }}
          />
        )
      },
      shouldCellUpdate: (curr, prev) =>
        !isEqual(curr.children, prev.children) || curr.email !== prev.email || curr.isCreating !== prev.isCreating,
    },
    // {
    //   title: t('work.resource.table.title.predictWorkload'),
    //   dataIndex: 'predictWorkload',
    //   key: 'predictWorkload',
    //   width: 80,
    //   render: (value, record) => {
    //     const isUserNotEmployee =
    //       record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
    //     const readonly =
    //       record.isArchived || record.type !== WorkResourceRowType.User || !canUpdate || isUserNotEmployee
    //     return (
    //       <ClickAwayInputNumber
    //         key='predict'
    //         readonly={readonly}
    //         required={false}
    //         isActive={record.isCreating}
    //         value={value}
    //         onUpdate={(v) => {
    //           const parents: DepartmentProjectWorkResourceInput[] = []
    //           getParentByKey(record.key, records, parents)
    //           const [parent] = parents
    //           const grandparents: DepartmentProjectWorkResourceInput[] = []
    //           getParentByKey(parent.key, records, grandparents)
    //           const [project] = grandparents

    //           updateRun({
    //             userWorkResourceSelfId: record.userWorkResourceSelfId,
    //             projectWorkResourceSelfId: project.projectWorkResourceSelfId,
    //             predictWorkload: Number(v),
    //           })
    //         }}
    //         onChange={(v) => {
    //           setRecords((prev) =>
    //             produce(prev, (draft) => {
    //               const currentRecords: DepartmentProjectWorkResourceInput[] = []
    //               getRecordByKey(record.key, draft, currentRecords)
    //               const [currentRecord] = currentRecords

    //               if (currentRecord) {
    //                 currentRecord.predictWorkload = !isNil(v) ? Number(v) : null
    //               }
    //             })
    //           )
    //         }}
    //       />
    //     )
    //   },
    //   shouldCellUpdate: (curr, prev) => {
    //     return Number(curr.predictWorkload) !== Number(prev.predictWorkload) || curr.isCreating !== prev.isCreating
    //   },
    // },
    {
      title: t('work.resource.table.title.actualWorkload'),
      dataIndex: 'actualWorkload',
      key: 'actualWorkload',
      width: 80,
      render: (value) => {
        return <span className={ss.readonlyText}>{!isNil(value) ? Number(value) : '待评估'}</span>
      },
      shouldCellUpdate: (curr, prev) =>
        Number(curr.actualWorkload) !== Number(prev.actualWorkload) || curr.isCreating !== prev.isCreating,
    },
    // {
    //   title: t('work.resource.table.title.period'),
    //   dataIndex: 'period',
    //   key: 'period',
    //   width: 240,
    //   shouldCellUpdate: (curr, prev) =>
    //     curr.startDate !== prev.startDate || curr.endDate !== prev.endDate || curr.isCreating !== prev.isCreating,
    //   render: (_, record) => {
    //     const isUserNotEmployee =
    //       record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
    //     const readonly =
    //       record.isArchived || record.type !== WorkResourceRowType.User || !canUpdate || isUserNotEmployee
    //     return (
    //       <ClickAwayRangePicker
    //         required={false}
    //         isActive={record.isCreating}
    //         readonly={readonly}
    //         startDate={record.startDate ? moment(record.startDate) : undefined}
    //         endDate={record.endDate ? moment(record.endDate) : undefined}
    //         onChange={(_, dateStrings) => {
    //           const [startDate, endDate] = dateStrings
    //           setRecords((prev) =>
    //             produce(prev, (draft) => {
    //               const currentRecords: DepartmentProjectWorkResourceInput[] = []
    //               getRecordByKey(record.key, draft, currentRecords)
    //               const [currentRecord] = currentRecords

    //               if (currentRecord) {
    //                 currentRecord.startDate = startDate
    //                 currentRecord.endDate = endDate
    //               }
    //             })
    //           )
    //           if (record.userWorkResourceSelfId) {
    //             const parents: DepartmentProjectWorkResourceInput[] = []
    //             getParentByKey(record.key, records, parents)
    //             const [parent] = parents
    //             const grandparents: DepartmentProjectWorkResourceInput[] = []
    //             getParentByKey(parent.key, records, grandparents)
    //             const [project] = grandparents

    //             updateRun({
    //               userWorkResourceSelfId: record.userWorkResourceSelfId,
    //               projectWorkResourceSelfId: project.projectWorkResourceSelfId,
    //               startDate: startDate,
    //               endDate: endDate,
    //             })
    //           }
    //         }}
    //       />
    //     )
    //   },
    // },
    // {
    //   title: '资源季度分布',
    //   dataIndex: 'quarterWorkload',
    //   key: 'quarterWorkload',
    //   shouldCellUpdate: (curr, prev) =>
    //     !isEqual(curr.quarters, prev.quarters) ||
    //     curr.startDate !== prev.startDate ||
    //     curr.endDate !== prev.endDate ||
    //     curr.isCreating !== prev.isCreating,
    //   render: (_, record) => {
    //     const allQuarters = !record.isCreating ? record.quarters.slice() : []
    //     const validQuartes: { year: string; quarter: string }[] = []
    //     if (!record.startDate && record.endDate) {
    //       const year = moment(record.endDate).year().toString()
    //       const quarter = `Q${moment(record.endDate).quarter().toString()}`
    //       const exist = allQuarters.some((q) => q.year === year && q.quarter === quarter)
    //       validQuartes.push({ year, quarter })
    //       if (!exist) {
    //         allQuarters.push({ year, quarter, workload: null })
    //       }
    //     } else if (!record.endDate && record.startDate) {
    //       const year = moment(record.startDate).year().toString()
    //       const quarter = `Q${moment(record.startDate).quarter().toString()}`
    //       const exist = allQuarters.some((q) => q.year === year && q.quarter === quarter)
    //       validQuartes.push({ year, quarter, workload: null })
    //       if (!exist) {
    //         allQuarters.push({ year, quarter, workload: null })
    //       }
    //     } else if (record.startDate && record.endDate) {
    //       const start = moment(record.startDate)
    //       const end = moment(record.endDate)
    //       let iter = start.clone()
    //       while (iter.isSameOrBefore(end)) {
    //         const year = iter.year().toString()
    //         const quarter = `Q${iter.quarter().toString()}`
    //         const exist = allQuarters.some((q) => q.year === year && q.quarter === quarter)
    //         validQuartes.push({ year, quarter, workload: null })
    //         if (!exist) {
    //           allQuarters.push({ year, quarter, workload: null })
    //         }
    //         iter = iter.add(1, 'quarter').startOf('quarter')
    //       }
    //     }

    //     allQuarters.sort((a, b) => {
    //       const aYear = Number(a.year)
    //       const aQuarter = Number(a.quarter[1])
    //       const bYear = Number(b.year)
    //       const bQuarter = Number(b.quarter[1])
    //       return aYear - bYear === 0 ? aQuarter - bQuarter : aYear - bYear
    //     })

    //     return (
    //       <Row
    //         gutter={[
    //           { xs: 8, sm: 16, md: 24 },
    //           { xs: 4, sm: 8, md: 12 },
    //         ]}
    //       >
    //         {allQuarters.map((quarterField) => {
    //           const isInTimeRange = validQuartes.some(
    //             (q) => q.year === quarterField.year && q.quarter === quarterField.quarter
    //           )
    //           const isArchived = isNumber(quarterField.archivedLevel) && quarterField.archivedLevel > 0
    //           const readonly = record.type !== WorkResourceRowType.User || isArchived
    //           return (
    //             <Col key={`${quarterField.year}-${quarterField.quarter}`}>
    //               <ClickAwayQuarterInputNumber
    //                 showLockedIcon={isArchived}
    //                 required={false}
    //                 key={`${quarterField.year}-${quarterField.quarter}`}
    //                 readonly={readonly}
    //                 disabled={!isInTimeRange}
    //                 isActive={record.isCreating}
    //                 year={quarterField.year}
    //                 quarter={quarterField.quarter}
    //                 value={quarterField}
    //                 onUpdate={(value) => {
    //                   if (record.userWorkResourceSelfId) {
    //                     const quarter = { ...quarterField, workload: Number(value) }

    //                     const parents: DepartmentProjectWorkResourceInput[] = []
    //                     getParentByKey(record.key, records, parents)
    //                     const [parent] = parents
    //                     const grandparents: DepartmentProjectWorkResourceInput[] = []
    //                     getParentByKey(parent.key, records, grandparents)
    //                     const [project] = grandparents

    //                     updateRun({
    //                       userWorkResourceSelfId: record.userWorkResourceSelfId,
    //                       projectWorkResourceSelfId: project.projectWorkResourceSelfId,
    //                       quarter,
    //                     })
    //                   }
    //                 }}
    //                 onChange={(value) => {
    //                   setRecords((prev) =>
    //                     produce(prev, (draft) => {
    //                       const currentRecords: DepartmentProjectWorkResourceInput[] = []
    //                       getRecordByKey(record.key, draft, currentRecords)
    //                       const [currentRecord] = currentRecords

    //                       if (currentRecord) {
    //                         const index = currentRecord.quarters.findIndex(
    //                           (q) => q.quarter === quarterField.quarter && q.year === quarterField.year
    //                         )
    //                         if (~index) {
    //                           currentRecord.quarters[index] = {
    //                             ...quarterField,
    //                             workload: Number(value),
    //                           }
    //                         } else {
    //                           currentRecord.quarters = [
    //                             ...currentRecord.quarters,
    //                             { ...quarterField, workload: Number(value) },
    //                           ]
    //                         }
    //                       }
    //                     })
    //                   )
    //                 }}
    //               />
    //             </Col>
    //           )
    //         })}
    //       </Row>
    //     )
    //   },
    // },
    {
      title: t('work.resource.table.title.weekWorkload'),
      dataIndex: 'weekWorkload',
      key: 'weekWorkload',
      width: 160,
      // cell 更新受到 weekDate state 影响，这里不检查更新逻辑
      // shouldCellUpdate: (curr, prev) =>
      //   !isEqual(curr, prev) ||
      //   curr.startDate !== prev.startDate ||
      //   curr.endDate !== prev.endDate ||
      //   curr.isCreating !== prev.isCreating,
      render: (_, record) => {
        const week = record.weeks.find(
          (week) => week.week === weekDate.week.toString() && week.year === weekDate.year.toString()
        ) || {
          year: weekDate.year.toString(),
          week: weekDate.week.toString(),
          startDate: weekDate.startDate,
          endDate: weekDate.endDate,
          workload: null,
        }

        const shouldWeekBeArchived =
          !getIsValidWeekToUpsert({
            year: Number(week.year),
            week: Number(week.week),
          }) || week.isArchived
        const isUserNotEmployee =
          record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
        const readonly =
          record.isArchived ||
          (!record.isCreating && record.type !== WorkResourceRowType.User) ||
          !canUpdate ||
          isUserNotEmployee ||
          shouldWeekBeArchived

        const existWeek = record?.weeks?.[0]
        return (
          <div className='flex gap-2 items-center'>
            <ClickAwayWeekInputNumber
              year={weekDate.year}
              week={weekDate.week}
              value={week}
              readonly={readonly}
              required={false}
              isActive={record.isCreating}
              showLockedIcon={shouldWeekBeArchived}
              onUpdate={(value) => {
                if (record.userWorkResourceSelfId) {
                  const parents: DepartmentProjectWorkResourceInput[] = []
                  getParentByKey(record.key, records, parents)
                  const [parent] = parents
                  const grandparents: DepartmentProjectWorkResourceInput[] = []
                  getParentByKey(parent.key, records, grandparents)
                  const [project] = grandparents

                  const weekToUpdate = { ...week, workload: Number(value) }
                  updateRun({
                    projectWorkResourceSelfId: project.projectWorkResourceSelfId,
                    userWorkResourceSelfId: record.userWorkResourceSelfId,
                    week: weekToUpdate,
                  })
                }
              }}
              onChange={(value) => {
                setRecords((prev) =>
                  produce(prev, (draft) => {
                    const currentRecords: DepartmentProjectWorkResourceInput[] = []
                    getRecordByKey(record.key, draft, currentRecords)
                    const [currentRecord] = currentRecords

                    if (currentRecord) {
                      const index = currentRecord.weeks.findIndex((q) => q.week === week.week && q.year === week.year)
                      if (~index) {
                        currentRecord.weeks[index] = {
                          ...week,
                          workload: Number(value),
                        }
                      } else {
                        currentRecord.weeks = [{ ...week, workload: Number(value) }]
                      }
                    }
                  })
                )
              }}
            />
            {record.type === WorkResourceRowType.User && existWeek?.comment?.comment && (
              <Tooltip title='备注'>
                <InfoCircleOutlined
                  className='cursor-pointer'
                  onClick={() => {
                    setOpenDrawer({ record, readonly: true })
                  }}
                />
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      title: t('work.resource.table.title.operation'),
      dataIndex: 'operation',
      key: 'operation',
      width: 200,
      // shouldCellUpdate: (curr, prev) => !isEqual(curr, prev) || curr.isCreating !== prev.isCreating,
      render: (_, record) => {
        const isUserNotEmployee =
          record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating

        if (record.type === WorkResourceRowType.User) {
          if (record.isArchived || isUserNotEmployee) {
            return <LockOutlined />
          }

          if (record.isCreating) {
            return (
              <div className='flex justify-between'>
                <Button
                  type='link'
                  onClick={() => {
                    const data = {
                      ...omit(record, ['isCreating', 'key']),
                      projectSelfId: record.project?.selfId,
                      resourceType: record.project?.resourceType,
                    }
                    run(data)
                    setRecords((prev) => {
                      const newState = produce(prev, (draft) => {
                        const parents: DepartmentProjectWorkResourceInput[] = []
                        getParentByKey(record.key, draft, parents)
                        const [parent] = parents
                        // 非 root 节点
                        if (parent) {
                          parent['children'] = (parent.children || []).filter((child) => child.key !== record.key)
                        } else {
                          // root 节点
                          return draft.filter((d) => d.key !== record.key)
                        }
                      })
                      return newState
                    })
                  }}
                >
                  {t('save')}
                </Button>
                <Button
                  type='link'
                  onClick={() => {
                    setRecords((prev) => {
                      const newState = produce(prev, (draft) => {
                        const parents: DepartmentProjectWorkResourceInput[] = []
                        getParentByKey(record.key, draft, parents)
                        const [parent] = parents
                        // 非 root 节点
                        if (parent) {
                          parent['children'] = (parent.children || []).filter((child) => child.key !== record.key)
                        } else {
                          // root 节点
                          return draft.filter((d) => d.key !== record.key)
                        }
                      })
                      return newState
                    })
                  }}
                >
                  {t('cancel')}
                </Button>
              </div>
            )
          }

          const commentButton =
            record.type === WorkResourceRowType.User ? (
              <Button
                type='link'
                onClick={() => {
                  setOpenDrawer({ record, readonly: false })
                }}
                disabled={!canUpdate}
              >
                {t('work.resource.comment')}
              </Button>
            ) : null

          const deleteButton = isSuperAdmin ? (
            <Popconfirm
              title={t(`work.resource.${record.type}.deleteConfirm`)}
              onConfirm={() => {
                const data = pick(record, ['userWorkResourceSelfId'])
                const parents: DepartmentProjectWorkResourceInput[] = []
                getParentByKey(record.key, records, parents)
                const [parent] = parents
                const grandparents: DepartmentProjectWorkResourceInput[] = []
                getParentByKey(parent.key, records, grandparents)
                const [project] = grandparents
                deleteRun({
                  ...data,
                  projectWorkResourceSelfId: project.projectWorkResourceSelfId,
                })
              }}
              disabled={!canDelete}
            >
              <Button type='text' danger disabled={!canDelete}>
                {t('remove')}
              </Button>
            </Popconfirm>
          ) : null

          const week = record.weeks.find(
            (week) => week.week === weekDate.week.toString() && week.year === weekDate.year.toString()
          )
          const invisibleButton =
            record.type === WorkResourceRowType.User &&
            canUpdate &&
            !record.invisibleDate &&
            !Number(week?.workload) &&
            moment().isBetween(weekDate.startDate, weekDate.endDate, 'date', '[]') ? (
              <Popconfirm
                title={
                  <div className='max-w-[300px]'>
                    <div className='whitespace-pre-wrap'>{t('work.resource.invisible.confirm1')}</div>
                    <div className='whitespace-pre-wrap'>{t('work.resource.invisible.confirm2')}</div>
                  </div>
                }
                onConfirm={() => {
                  const parents: DepartmentProjectWorkResourceInput[] = []
                  getParentByKey(record.key, records, parents)
                  const [parent] = parents
                  const grandparents: DepartmentProjectWorkResourceInput[] = []
                  getParentByKey(parent.key, records, grandparents)
                  const [project] = grandparents
                  updateRun({
                    userWorkResourceSelfId: record.userWorkResourceSelfId,
                    projectWorkResourceSelfId: project.projectWorkResourceSelfId,
                    invisibleDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
                  })
                }}
              >
                <Button type='link'>{t('work.resource.invisible.title')}</Button>
              </Popconfirm>
            ) : null

          return (
            <div className='flex flex-wrap gap-2'>
              {commentButton}
              {invisibleButton}
              {deleteButton}
            </div>
          )
        }
      },
    },
  ]

  return (
    <>
      <Table
        rowClassName={(record) => {
          if (record.subprojectWorkResourceSelfId) {
            return s.departmentRow
          }
          if (record.projectWorkResourceSelfId) {
            return s.subprojectRow
          }
          return ''
        }}
        expandable={{
          expandedRowKeys: expandKeys,
          onExpandedRowsChange: (keys) => setExpandKeys(keys as string[]),
        }}
        components={{
          body: { cell: TdCell },
        }}
        loading={getLoading || creatingLoading || deleteLoading || updateLoading}
        pagination={false}
        bordered
        columns={columns}
        dataSource={records}
      />
      <Drawer
        title={openDrawer?.readonly ? '备注详情' : '填写备注'}
        placement='right'
        onClose={() => setOpenDrawer(undefined)}
        visible={!!openDrawer}
      >
        <Input.TextArea
          readOnly={openDrawer?.readonly}
          value={drawerContent}
          onChange={(e) => setDrawerContent(e.target.value)}
          placeholder={t('work.resource.commentPlaceholder')}
          autoSize={{ minRows: 8 }}
        />
        {!openDrawer?.readonly && (
          <div className='flex my-4 justify-end'>
            <Button
              loading={commentLoading}
              type='primary'
              onClick={() => {
                const week = openDrawer?.record?.weeks?.[0]

                commentRun({
                  userWorkResourceSelfId: openDrawer?.record?.userWorkResourceSelfId,
                  year: weekDate.year.toString(),
                  week: weekDate.week.toString(),
                  startDate: weekDate.startDate,
                  endDate: weekDate.endDate,
                  userWeekWorkResourceId: week?.id,
                  comment: drawerContent,
                })
                setOpenDrawer(undefined)
              }}
            >
              {t('submit')}
            </Button>
          </div>
        )}
      </Drawer>
    </>
  )
})
